
/**
 * Bcp Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';
import { teamAssociationServiceMethods } from './team-association.mixin.js';

export class BcpService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
        Object.assign(this, teamAssociationServiceMethods());
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                pagination: response.pagination || response.meta || {},
                message: response.message || 'Bcp listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch bcp list');
        }
    }

    async getStats() {
        try {
            const response = await withRetry(() => this.api.getStats(), { maxRetries: 2 });
            const payload = response?.data !== undefined ? response.data : response;
            return {
                success: true,
                data: payload,
                message: response?.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch bcp stats');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch bcp');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Bcp oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create bcp');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Bcp güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update bcp');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Bcp silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete bcp');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Delete multiple items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async batchDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                deletedCount: response.deletedCount ?? response.data?.deletedCount ?? 0,
                message: response.message || response.data?.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch delete bcps');
        }
    }

    // --- Relationship Methods ---

    /**
     * Get related initial response steps
     */
    async getRelatedInitialResponse(id, params = {}) {
        try {
            const response = await this.api.getRelatedInitialResponse(id, params);
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related initial response steps');
        }
    }

    /**
     * Add initial response steps to BCP
     */
    async addInitialResponseSteps(id, stepIds) {
        try {
            const response = await this.api.addInitialResponseSteps(id, stepIds);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link initial response steps');
        }
    }

    /**
     * Remove initial response step from BCP
     */
    async removeInitialResponseStep(id, stepId) {
        try {
            const response = await this.api.removeInitialResponseStep(id, stepId);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink initial response step');
        }
    }

    /**
     * Get related documents
     */
    async getRelatedDocuments(id, params = {}) {
        try {
            const response = await this.api.getRelatedDocuments(id, params);
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related documents');
        }
    }

    /**
     * Add documents to BCP
     */
    async addDocuments(id, documentIds) {
        try {
            const response = await this.api.addDocuments(id, documentIds);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link documents');
        }
    }

    /**
     * Remove document from BCP
     */
    async removeDocument(id, documentId) {
        try {
            const response = await this.api.removeDocument(id, documentId);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink document');
        }
    }

    // --- Relationship: Exercises Methods ---

    async getRelatedExercises(id, params = {}) {
        try {
            const response = await this.api.getRelatedExercises(id, params);
            return { success: true, data: response.data, pagination: response.pagination || {}, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related exercises'); }
    }

    async addExercises(id, exerciseIds) {
        try {
            const response = await this.api.addExercises(id, exerciseIds);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to link exercises'); }
    }

    async removeExercise(id, exerciseId) {
        try {
            const response = await this.api.removeExercise(id, exerciseId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to unlink exercise'); }
    }

    async getRelatedBias(id, params = {}) {
        try {
            const response = await this.api.getRelatedBias(id, params);
            return { success: true, data: response.data, pagination: response.pagination || {}, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related BIAs'); }
    }

    async addBias(id, biaIds) {
        try {
            const response = await this.api.addBias(id, biaIds);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to link BIAs'); }
    }

    async removeBia(id, biaId) {
        try {
            const response = await this.api.removeBia(id, biaId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to unlink BIA'); }
    }

    // --- In Scope: Process Relationship Methods ---

    async getRelatedProcesses(id, params = {}) {
        try {
            const response = await this.api.getRelatedProcesses(id, params);
            return { success: true, data: response.data, pagination: response.pagination || {}, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related processes'); }
    }

    async addProcesses(id, processIds) {
        try {
            const response = await this.api.addProcesses(id, processIds);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to link processes'); }
    }

    async removeProcess(id, processId) {
        try {
            const response = await this.api.removeProcess(id, processId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to unlink process'); }
    }

    // --- In Scope: Asset Relationship Methods ---

    async getRelatedAssets(id, params = {}) {
        try {
            const response = await this.api.getRelatedAssets(id, params);
            return { success: true, data: response.data, pagination: response.pagination || {}, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related assets'); }
    }

    async addAssets(id, assetIds) {
        try {
            const response = await this.api.addAssets(id, assetIds);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to link assets'); }
    }

    async removeAsset(id, assetId) {
        try {
            const response = await this.api.removeAsset(id, assetId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to unlink asset'); }
    }

    // --- In Scope: Vendor Relationship Methods ---

    async getRelatedVendors(id, params = {}) {
        try {
            const response = await this.api.getRelatedVendors(id, params);
            return { success: true, data: response.data, pagination: response.pagination || {}, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related vendors'); }
    }

    async addVendors(id, vendorIds) {
        try {
            const response = await this.api.addVendors(id, vendorIds);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to link vendors'); }
    }

    async removeVendor(id, vendorId) {
        try {
            const response = await this.api.removeVendor(id, vendorId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) { throw this._handleError(error, 'Failed to unlink vendor'); }
    }

    // --- Communication Relationship Methods ---

    async getRelatedCommunications(id, params = {}) {
        try {
            const response = await this.api.getRelatedCommunications(id, params);
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related communication contacts');
        }
    }

    async addCommunications(id, communicationIds) {
        try {
            const response = await this.api.addCommunications(id, communicationIds);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link communication contacts');
        }
    }

    async removeCommunication(id, communicationId) {
        try {
            const response = await this.api.removeCommunication(id, communicationId);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink communication contact');
        }
    }

    // --- Communication Plan Relationship Methods ---

    async getRelatedCommunicationPlans(id, params = {}) {
        try {
            const response = await this.api.getRelatedCommunicationPlans(id, params);
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related communication plans');
        }
    }

    async addCommunicationPlans(id, planIds) {
        try {
            const response = await this.api.addCommunicationPlans(id, planIds);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link communication plans');
        }
    }

    async removeCommunicationPlan(id, planId) {
        try {
            const response = await this.api.removeCommunicationPlan(id, planId);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink communication plan');
        }
    }

    // --- Locations Relationship Methods ---

    /**
     * Get related locations
     */
    async getRelatedLocations(id, params = {}) {
        try {
            const response = await this.api.getRelatedLocations(id, params);
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related locations');
        }
    }

    /**
     * Add locations to BCP
     */
    async addLocations(id, locations) {
        try {
            const response = await this.api.addLocations(id, locations);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link locations');
        }
    }

    /**
     * Update location relation in BCP
     */
    async updateLocation(id, locationId, data) {
        try {
            const response = await this.api.updateLocation(id, locationId, data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Location relation updated'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update location relation');
        }
    }

    /**
     * Remove location from BCP
     */
    async removeLocation(id, locationId) {
        try {
            const response = await this.api.removeLocation(id, locationId);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink location');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }

    async getPartyTemplate(params = {}) {
        try {
            const response = await this.api.getPartyTemplate(params);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to load party template');
        }
    }

    async getParty(id) {
        try {
            const response = await this.api.getParty(id);
            return {
                success: true,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch party instance');
        }
    }

    async syncParty(id, body) {
        try {
            const response = await this.api.syncParty(id, body);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to sync party');
        }
    }

    async submit(id) {
        try {
            const response = await this.api.submit(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to submit BCP');
        }
    }
}
